import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Tabs,
  Table,
  Space,
  Input,
  Select,
  Switch,
  Typography,
  Spin,
} from "antd";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import CodeEditor from "../common/code-editor";
import {
  getFileData,
  getFileList,
  deleteSchema,
  saveDraft,
  publishDraft,
  enableWidget,
  disableWidget,
} from "./ui-widget-service";
import { useRecoilState } from "recoil";
import { developerAccessTokenState } from "../global-state";
import { useToast } from "../common/toast";

const { Option } = Select;
const { Text } = Typography;

const UiWidgetView = () => {
  const [widgets, setWidgets] = useState([]);
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [metaContent, setMetaContent] = useState("");
  const [scriptContent, setScriptContent] = useState("");
  const { successToast, warningToast, errorToast } = useToast();

  const [currentMetaContent, setCurrentMetaContent] = useState("");
  const [currentScriptContent, setCurrentScriptContent] = useState("");
  const [currentTwinTag, setCurrentTwinTag] = useState("");
  const [currentVersion, setCurrentVersion] = useState("");

  const canSave = metaContent.trim() !== "" && scriptContent.trim() !== "";
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmBatchDeleteOpen, setConfirmBatchDeleteOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(null);
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState,
  );
  const [statusLoading, setStatusLoading] = useState(false);
  const [contentTab, setContentTab] = useState("0");
  const [searchTerm, setSearchTerm] = useState("");
  const [envFilter, setEnvFilter] = useState("both");
  const [deleTag, setDeleTag] = useState("");
  const [selectedWidgetKeys, setSelectedWidgetKeys] = useState([]);
  const [localLoadOpen, setLocalLoadOpen] = useState(false);
  const [loadedWidgetFiles, setLoadedWidgetFiles] = useState([]);
  const [selectedLoadedWidgetKey, setSelectedLoadedWidgetKey] = useState("");
  const [loadedContentTab, setLoadedContentTab] = useState("meta");
  const fileInputRef = useRef(null);

  const fetchFileList = async () => {
    setStatusLoading(true);
    try {
      const result = await getFileList(developerAccessToken);

      const resp = result?.items;
      if (Array.isArray(resp) && resp.length > 0) {
        // Transform data to match structure
        const widgetsList = resp
          .map(item => {
            const versions = [];

            // Add draft version if exists
            if (item.draftVersion) {
              versions.push({
                env: "draft",
                version: item.draftVersion,
              });
            }

            // Add versions from the versions array
            if (item.versions && Array.isArray(item.versions)) {
              item.versions.forEach(versionStr => {
                // The version string is the environment (e.g., "prod", "stg")
                versions.push({
                  env: versionStr,
                  version: versionStr,
                });
              });
            }

            return {
              twinTag: item.twinTag,
              versions: versions,
              active: item.active !== undefined ? item.active : true, // Active status from API
            };
          })
          .sort((a, b) => a.twinTag.localeCompare(b.twinTag));

        setWidgets(widgetsList);
      } else {
        setWidgets([]);
      }
    } catch (error) {
      errorToast("Fetch Error!");
      console.error("Error fetching file list:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchFileList();
  }, []);

  useEffect(() => {
    setSelectedWidgetKeys(prevKeys =>
      prevKeys.filter(key =>
        widgets.some(
          widget =>
            widget.twinTag === key &&
            (hasStgVersion(widget) || hasProdVersion(widget)),
        ),
      ),
    );
  }, [widgets]);

  const hasStgVersion = widget =>
    widget.versions.some(version => version.env === "stg");

  const hasProdVersion = widget =>
    widget.versions.some(version => version.env === "prod");

  const getDeleteVersion = widget => {
    if (hasStgVersion(widget)) {
      return "stg";
    }
    if (hasProdVersion(widget)) {
      return "prod";
    }
    return null;
  };

  const handleOpenWidget = (twinTag, env) => {
    const widget = widgets.find(w => w.twinTag === twinTag);
    if (widget) {
      const versionInfo = widget.versions.find(v => v.env === env);
      if (versionInfo) {
        fetchFileContent(twinTag, versionInfo.version);
        setContentDialogOpen(true);
      }
    }
  };

  const clearCurrentContent = () => {
    setCurrentMetaContent("");
    setCurrentScriptContent("");
    setCurrentTwinTag("");
    setCurrentVersion("");
  };

  const clearContent = () => {
    setMetaContent("");
    setScriptContent("");
  };

  const fetchFileContent = async (twinTag, version) => {
    try {
      const resp = await getFileData(twinTag, version, developerAccessToken);
      if (resp) {
        setCurrentTwinTag(twinTag || "");
        setCurrentVersion(version || "");
        setCurrentMetaContent(resp.meta || "");
        setCurrentScriptContent(resp.script || "");
      } else {
        clearCurrentContent();
      }
    } catch (error) {
      console.error("Error fetching file content:", error);
      clearCurrentContent();
    }
  };

  const handleAddCancel = () => {
    setAddDialogOpen(false);
    clearContent();
    setCurrentStep(1);
  };

  const handleUpsetCancel = () => {
    setContentDialogOpen(false);
    clearCurrentContent();
  };

  const handleUpsetFiles = async () => {
    if (!currentMetaContent.trim() || !currentScriptContent.trim()) {
      handleUpsetCancel();
      errorToast("Content cant not be empty!");
      return;
    }

    setIsRunning("UPSET_FILE");

    try {
      const resp = await saveDraft(
        currentMetaContent,
        currentScriptContent,
        developerAccessToken,
      );

      if (resp) {
        successToast("Save Success!");
        await fetchFileList();
      } else {
        errorToast("Save Error!");
      }
    } catch (error) {
      errorToast("Save Error!");
      console.error("Error uploading files:", error);
    } finally {
      handleUpsetCancel();
      setIsRunning(null);
    }
  };

  const handleAddFiles = async () => {
    if (!metaContent.trim() || !scriptContent.trim()) {
      errorToast("Content cant not be empty!");
      handleAddCancel();
      return;
    }

    setIsRunning("ADD_FILE");

    try {
      const resp = await saveDraft(
        metaContent,
        scriptContent,
        developerAccessToken,
      );

      if (resp) {
        await fetchFileList();
        successToast("Add success!");
      } else {
        errorToast("Add Error!");
      }
    } catch (error) {
      errorToast("Error add files:", error);
      console.error("Error add files:", error);
    } finally {
      setIsRunning(null);
      handleAddCancel();
    }
  };

  const handleDelete = async () => {
    if (!deleTag) {
      setConfirmDeleteOpen(false);
      errorToast("File does not exist!");
      return;
    }

    const widget = widgets.find(item => item.twinTag === deleTag);
    const deleteVersion = widget ? getDeleteVersion(widget) : null;
    if (!deleteVersion) {
      setConfirmDeleteOpen(false);
      errorToast("No stg/prod version can be deleted.");
      return;
    }

    setIsRunning(`DELE_FILE_${deleTag}`);

    try {
      const success = await deleteSchema(
        deleTag,
        deleteVersion,
        developerAccessToken,
      );
      console.log(success);
      if (success) {
        await fetchFileList();
        successToast("Delete Success!");
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast(error);
      console.error("Error deleting file:", error);
    } finally {
      setIsRunning(null);
      setConfirmDeleteOpen(false);
      setDeleTag("");
    }
  };

  const handleBatchDelete = async () => {
    const selectedWidgets = widgets.filter(widget =>
      selectedWidgetKeys.includes(widget.twinTag),
    );

    if (selectedWidgets.length === 0) {
      warningToast("No selected widgets.");
      setConfirmBatchDeleteOpen(false);
      return;
    }

    setIsRunning("BATCH_DELETE");
    let successCount = 0;
    const failedTwinTags = [];

    for (const widget of selectedWidgets) {
      const deleteVersion = getDeleteVersion(widget);
      if (!deleteVersion) {
        failedTwinTags.push(widget.twinTag);
        continue;
      }
      try {
        const resp = await deleteSchema(
          widget.twinTag,
          deleteVersion,
          developerAccessToken,
        );
        if (resp) {
          successCount += 1;
        } else {
          failedTwinTags.push(widget.twinTag);
        }
      } catch (error) {
        failedTwinTags.push(widget.twinTag);
      }
    }

    setIsRunning(null);
    setConfirmBatchDeleteOpen(false);
    await fetchFileList();

    if (failedTwinTags.length === 0) {
      successToast(`Deleted ${successCount} widget(s).`);
      return;
    }

    warningToast(
      `Deleted ${successCount}. Failed: ${failedTwinTags
        .slice(0, 5)
        .join(", ")}${failedTwinTags.length > 5 ? " ..." : ""}`,
    );
  };

  const handleContentTabChange = key => {
    setContentTab(key);
  };

  const parseLocalWidgetFiles = async fileList => {
    const groupedByTwinTag = {};
    const invalidFiles = [];

    const parsedFiles = await Promise.all(
      Array.from(fileList).map(async file => {
        const content = await file.text();
        return {
          name: file.name,
          content,
        };
      }),
    );

    parsedFiles.forEach(file => {
      const match = file.name.match(/^(.*)-(meta|script)\.ya?ml$/i);
      if (!match) {
        invalidFiles.push(file.name);
        return;
      }

      const twinTag = match[1];
      const fileType = match[2].toLowerCase();

      if (!groupedByTwinTag[twinTag]) {
        groupedByTwinTag[twinTag] = {
          twinTag,
          metaFileName: "",
          scriptFileName: "",
          metaContent: "",
          scriptContent: "",
        };
      }

      if (fileType === "meta") {
        groupedByTwinTag[twinTag].metaFileName = file.name;
        groupedByTwinTag[twinTag].metaContent = file.content;
      }

      if (fileType === "script") {
        groupedByTwinTag[twinTag].scriptFileName = file.name;
        groupedByTwinTag[twinTag].scriptContent = file.content;
      }
    });

    const completePairs = [];
    const incompletePairs = [];

    Object.values(groupedByTwinTag).forEach(item => {
      if (item.metaContent && item.scriptContent) {
        completePairs.push({
          ...item,
          key: item.twinTag,
        });
      } else {
        incompletePairs.push(item.twinTag);
      }
    });

    return {
      completePairs: completePairs.sort((a, b) =>
        a.twinTag.localeCompare(b.twinTag),
      ),
      invalidFiles,
      incompletePairs,
    };
  };

  const handleLocalFilesPicked = async event => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) {
      return;
    }

    try {
      const { completePairs, invalidFiles, incompletePairs } =
        await parseLocalWidgetFiles(fileList);

      if (invalidFiles.length > 0) {
        warningToast(
          `Ignored invalid files: ${invalidFiles.slice(0, 5).join(", ")}${
            invalidFiles.length > 5 ? " ..." : ""
          }`,
        );
      }

      if (incompletePairs.length > 0) {
        warningToast(
          `Ignored incomplete pairs: ${incompletePairs.slice(0, 5).join(", ")}${
            incompletePairs.length > 5 ? " ..." : ""
          }`,
        );
      }

      if (completePairs.length === 0) {
        errorToast(
          "No valid widget pair found. Need both xxx-meta.yml and xxx-script.yml.",
        );
        return;
      }

      setLoadedWidgetFiles(completePairs);
      setSelectedLoadedWidgetKey(completePairs[0].key);
      setLoadedContentTab("meta");
      setLocalLoadOpen(true);
      successToast(`Loaded ${completePairs.length} widget pair(s).`);
    } catch (error) {
      console.error("Error loading local files:", error);
      errorToast("Error loading local files.");
    } finally {
      event.target.value = "";
    }
  };

  const handleSaveAllLoadedFiles = async () => {
    if (loadedWidgetFiles.length === 0) {
      warningToast("No loaded files to save.");
      return;
    }

    setIsRunning("SAVE_ALL_LOCAL");
    let successCount = 0;
    const failedTwinTags = [];

    for (const widgetFile of loadedWidgetFiles) {
      try {
        const resp = await saveDraft(
          widgetFile.metaContent,
          widgetFile.scriptContent,
          developerAccessToken,
        );
        if (resp) {
          successCount += 1;
        } else {
          failedTwinTags.push(widgetFile.twinTag);
        }
      } catch (error) {
        failedTwinTags.push(widgetFile.twinTag);
      }
    }

    setIsRunning(null);

    if (failedTwinTags.length === 0) {
      successToast(`Saved ${successCount} widget(s) to draft.`);
      await fetchFileList();
      return;
    }

    warningToast(
      `Saved ${successCount}. Failed: ${failedTwinTags.slice(0, 5).join(", ")}${
        failedTwinTags.length > 5 ? " ..." : ""
      }`,
    );
    await fetchFileList();
  };

  const handlePublishDraft = async twinTag => {
    setIsRunning(`PUB_DRAFT_${twinTag}`);
    try {
      const resp = await publishDraft(twinTag, developerAccessToken);
      if (resp) {
        successToast("Publish Success!");
        await fetchFileList();
      }
    } catch (error) {
      errorToast(error);
    } finally {
      setIsRunning(null);
    }
  };

  const handleBulkPublishStg = async () => {
    const selectedWidgets = widgets.filter(widget =>
      selectedWidgetKeys.includes(widget.twinTag),
    );
    const stgWidgets = selectedWidgets.filter(widget =>
      widget.versions.some(version => version.env === "stg"),
    );

    if (stgWidgets.length === 0) {
      warningToast("No selected widgets contain stg version.");
      return;
    }

    setIsRunning("BULK_PUBLISH_STG");
    let successCount = 0;
    const failedTwinTags = [];

    for (const widget of stgWidgets) {
      try {
        const resp = await publishDraft(widget.twinTag, developerAccessToken);
        if (resp) {
          successCount += 1;
        } else {
          failedTwinTags.push(widget.twinTag);
        }
      } catch (error) {
        failedTwinTags.push(widget.twinTag);
      }
    }

    setIsRunning(null);
    await fetchFileList();

    if (failedTwinTags.length === 0) {
      successToast(`Published ${successCount} stg widget(s).`);
      return;
    }

    warningToast(
      `Published ${successCount}. Failed: ${failedTwinTags
        .slice(0, 5)
        .join(", ")}${failedTwinTags.length > 5 ? " ..." : ""}`,
    );
  };

  const handleToggleActive = async (twinTag, version, currentActive) => {
    const toggleKey = `TOGGLE_${twinTag}_${version}`;
    setIsRunning(toggleKey);
    try {
      const resp = currentActive
        ? await disableWidget(twinTag, version, developerAccessToken)
        : await enableWidget(twinTag, version, developerAccessToken);

      if (resp) {
        successToast(
          currentActive ? "Disabled Successfully!" : "Enabled Successfully!",
        );
        await fetchFileList();
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast(error);
      console.error("Error toggling active status:", error);
    } finally {
      setIsRunning(null);
    }
  };

  const filteredWidgets = widgets.filter(widget => {
    // Apply search filter
    const matchesSearch = widget.twinTag
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Apply environment filter
    if (envFilter === "both") return true;

    // Check if widget has the selected environment
    return widget.versions.some(version => version.env === envFilter);
  });

  const getVersionButtons = (twinTag, versions) => {
    const prodVersion = versions.find(v => v.env === "prod");
    const stgVersion = versions.find(v => v.env === "stg");
    const draftVersion = versions.find(v => v.env === "draft");

    return (
      <Space wrap>
        {draftVersion && (
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={() => handleOpenWidget(twinTag, "draft")}
              style={{ backgroundColor: "#1890ff" }}
            >
              Draft
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => handlePublishDraft(twinTag)}
              disabled={isRunning === `PUB_DRAFT_${twinTag}`}
              loading={isRunning === `PUB_DRAFT_${twinTag}`}
              style={{ backgroundColor: "#52c41a" }}
            >
              Publish
            </Button>
          </Space>
        )}
        {prodVersion && (envFilter === "both" || envFilter === "prod") && (
          <Button
            size="small"
            type="primary"
            onClick={() => handleOpenWidget(twinTag, "prod")}
          >
            Prod
          </Button>
        )}
        {stgVersion && (envFilter === "both" || envFilter === "stg") && (
          <Button
            size="small"
            type="default"
            onClick={() => handleOpenWidget(twinTag, "stg")}
          >
            Stg
          </Button>
        )}
      </Space>
    );
  };

  const getActiveToggle = (twinTag, versions, active) => {
    // Only show toggle for prod/stg versions, not draft
    const activeVersions = versions.filter(v => v.env !== "draft");

    if (activeVersions.length === 0) {
      return <Text type="secondary">N/A</Text>;
    }

    // Use the first active version (prod or stg)
    const version = activeVersions[0];
    const toggleKey = `TOGGLE_${twinTag}_${version.version}`;

    return (
      <Switch
        checked={active}
        onChange={() => handleToggleActive(twinTag, version.version, active)}
        disabled={isRunning === toggleKey}
      />
    );
  };

  const columns = [
    {
      title: "Twin Tag",
      dataIndex: "twinTag",
      key: "twinTag",
      width: "45%",
      render: text => <Text style={{ fontFamily: "monospace" }}>{text}</Text>,
    },
    {
      title: "Versions",
      dataIndex: "versions",
      key: "versions",
      width: "30%",
      render: (versions, record) =>
        getVersionButtons(record.twinTag, versions),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      width: "10%",
      render: (active, record) =>
        getActiveToggle(record.twinTag, record.versions, active),
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      render: (_, record) => (
        <Button
          size="small"
          danger
          type="primary"
          onClick={() => {
            setDeleTag(record.twinTag);
            setConfirmDeleteOpen(true);
          }}
          disabled={
            !getDeleteVersion(record) || isRunning === `DELE_FILE_${record.twinTag}`
          }
          loading={isRunning === `DELE_FILE_${record.twinTag}`}
        >
          Delete
        </Button>
      ),
    },
  ];

  const loadedFilesColumns = [
    {
      title: "Twin Tag",
      dataIndex: "twinTag",
      key: "twinTag",
      width: "35%",
      render: text => <Text style={{ fontFamily: "monospace" }}>{text}</Text>,
    },
    {
      title: "Meta File",
      dataIndex: "metaFileName",
      key: "metaFileName",
      width: "32.5%",
    },
    {
      title: "Script File",
      dataIndex: "scriptFileName",
      key: "scriptFileName",
      width: "32.5%",
    },
  ];

  const selectedLoadedWidget = loadedWidgetFiles.find(
    file => file.key === selectedLoadedWidgetKey,
  );
  const selectedWidgets = widgets.filter(widget =>
    selectedWidgetKeys.includes(widget.twinTag),
  );
  const selectedStgCount = selectedWidgets.filter(widget =>
    hasStgVersion(widget),
  ).length;
  const pendingDeleteWidget = widgets.find(widget => widget.twinTag === deleTag);
  const pendingDeleteVersion = pendingDeleteWidget
    ? getDeleteVersion(pendingDeleteWidget)
    : null;
  const selectedDeleteTargets = selectedWidgets
    .map(widget => ({
      twinTag: widget.twinTag,
      version: getDeleteVersion(widget),
    }))
    .filter(item => item.version !== null);

  return (
    <div style={{ padding: 16 }}>
      {statusLoading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Filter and action buttons row */}
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Select
              value={envFilter}
              onChange={value => setEnvFilter(value)}
              style={{ minWidth: 120 }}
              size="middle"
            >
              <Option value="both">Any</Option>
              <Option value="prod">Prod</Option>
              <Option value="stg">Stg</Option>
            </Select>
            <Button type="default" onClick={() => fetchFileList()}>
              Reload
            </Button>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              Load Files
            </Button>
            <Button type="primary" onClick={() => setAddDialogOpen(true)}>
              Add UI
            </Button>
            <Input
              placeholder="Filter by Twin Tag..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ flex: 1 }}
              allowClear
            />
          </div>

          <div
            style={{
              marginBottom: 12,
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <Text type="secondary">
              Selected: {selectedWidgetKeys.length} row
              {selectedWidgetKeys.length !== 1 ? "s" : ""}
            </Text>
            <Button
              type="primary"
              onClick={handleBulkPublishStg}
              disabled={selectedStgCount === 0}
              loading={isRunning === "BULK_PUBLISH_STG"}
            >
              Publish Selected Stg ({selectedStgCount})
            </Button>
            <Button
              danger
              onClick={() => setConfirmBatchDeleteOpen(true)}
              disabled={selectedWidgetKeys.length === 0}
              loading={isRunning === "BATCH_DELETE"}
            >
              Delete Selected ({selectedWidgetKeys.length})
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={filteredWidgets}
            rowKey="twinTag"
            rowSelection={{
              selectedRowKeys: selectedWidgetKeys,
              onChange: selectedRowKeys => {
                setSelectedWidgetKeys(selectedRowKeys);
              },
              getCheckboxProps: record => ({
                disabled: !(hasStgVersion(record) || hasProdVersion(record)),
              }),
            }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            locale={{
              emptyText: searchTerm
                ? "No widgets match your search"
                : "No widgets found",
            }}
          />

          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              Total: {filteredWidgets.length} widget
              {filteredWidgets.length !== 1 ? "s" : ""}
            </Text>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".yml,.yaml"
            multiple
            style={{ display: "none" }}
            onChange={handleLocalFilesPicked}
          />
        </>
      )}

      <Modal
        open={addDialogOpen}
        onCancel={handleAddCancel}
        width={800}
        title={
          currentStep === 1
            ? "Enter File Name"
            : currentStep === 2
            ? "Edit Meta Content"
            : "Edit Script Content"
        }
        footer={null}
        closeIcon={<CloseOutlined />}
      >
        {currentStep === 1 && (
          <div>
            <h5 style={{ marginBottom: 16 }}>Meta Content</h5>
            <CodeEditor
              value={metaContent}
              editable={true}
              onChange={instance => {
                const code = instance.getValue();
                setMetaContent(code);
              }}
              height="400px"
            />
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Space>
                <Button onClick={handleAddCancel}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={() => setCurrentStep(2)}
                  disabled={metaContent.trim() === ""}
                >
                  Next
                </Button>
              </Space>
            </div>
          </div>
        )}
        {currentStep === 2 && (
          <div>
            <h5 style={{ marginBottom: 16 }}>Script Content</h5>
            <CodeEditor
              value={scriptContent}
              editable={true}
              onChange={instance => {
                const code = instance.getValue();
                setScriptContent(code);
              }}
              height="400px"
            />
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Space>
                <Button onClick={handleAddCancel}>Cancel</Button>
                <Button onClick={() => setCurrentStep(1)}>Back</Button>
                <Button
                  type="primary"
                  onClick={handleAddFiles}
                  disabled={!canSave}
                  loading={isRunning === "ADD_FILE"}
                >
                  Save
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDeleteOpen}
        onCancel={() => setConfirmDeleteOpen(false)}
        width={400}
        title="Warning"
        footer={[
          <Button key="cancel" onClick={() => setConfirmDeleteOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleDelete}
            loading={isRunning === `DELE_FILE_${deleTag}`}
          >
            Confirm
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete <b>{deleTag}</b> (
          <b>{pendingDeleteVersion || "unknown"}</b>)?
        </div>
      </Modal>

      <Modal
        open={confirmBatchDeleteOpen}
        onCancel={() => setConfirmBatchDeleteOpen(false)}
        width={420}
        title="Warning"
        footer={[
          <Button
            key="cancel_batch"
            onClick={() => setConfirmBatchDeleteOpen(false)}
          >
            Cancel
          </Button>,
          <Button
            key="confirm_batch"
            type="primary"
            danger
            onClick={handleBatchDelete}
            loading={isRunning === "BATCH_DELETE"}
          >
            Confirm
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete {selectedDeleteTargets.length} selected
          widget(s)?
        </div>
        <div style={{ marginTop: 8 }}>
          {selectedDeleteTargets.slice(0, 8).map(item => (
            <div key={item.twinTag}>
              {item.twinTag} ({item.version})
            </div>
          ))}
          {selectedDeleteTargets.length > 8 && (
            <div>... and {selectedDeleteTargets.length - 8} more</div>
          )}
        </div>
      </Modal>

      <Modal
        open={contentDialogOpen}
        onCancel={() => handleUpsetCancel()}
        width="90%"
        style={{ maxWidth: 1400 }}
        title="File Content"
        footer={null}
        closeIcon={<CloseOutlined />}
      >
        <div>
          {/* Action buttons */}
          <div style={{ marginBottom: 16, display: "flex", gap: 16 }}>
            {currentVersion === "stg" && (
              <Button
                type="primary"
                style={{ backgroundColor: "#52c41a" }}
                onClick={() => handlePublishDraft(currentTwinTag)}
                disabled={isRunning === `PUB_DRAFT_${currentTwinTag}`}
                loading={isRunning === `PUB_DRAFT_${currentTwinTag}`}
              >
                Publish to Prod
              </Button>
            )}
            <Button
              type="primary"
              onClick={async () => {
                handleUpsetFiles();
              }}
              disabled={!currentScriptContent || !currentMetaContent}
              loading={isRunning === "UPSET_FILE"}
            >
              Save
            </Button>
          </div>

          <Tabs
            activeKey={contentTab}
            onChange={handleContentTabChange}
            items={[
              {
                key: "0",
                label: "Meta",
                children: (
                  <div style={{ flexGrow: 1 }}>
                    <CodeEditor
                      value={currentMetaContent}
                      editable={true}
                      height="800px"
                      onChange={instance => {
                        const code = instance.getValue();
                        setCurrentMetaContent(code);
                      }}
                    />
                  </div>
                ),
              },
              {
                key: "1",
                label: "Script",
                children: (
                  <div style={{ flexGrow: 1 }}>
                    <CodeEditor
                      value={currentScriptContent}
                      editable={true}
                      height="800px"
                      onChange={instance => {
                        const code = instance.getValue();
                        setCurrentScriptContent(code);
                      }}
                    />
                  </div>
                ),
              },
            ]}
          />
        </div>
      </Modal>

      <Modal
        open={localLoadOpen}
        onCancel={() => setLocalLoadOpen(false)}
        width="90%"
        style={{ maxWidth: 1400 }}
        title="Loaded Local Widget Files"
        closeIcon={<CloseOutlined />}
        footer={[
          <Button key="close" onClick={() => setLocalLoadOpen(false)}>
            Close
          </Button>,
          <Button
            key="save_all"
            type="primary"
            onClick={handleSaveAllLoadedFiles}
            disabled={loadedWidgetFiles.length === 0}
            loading={isRunning === "SAVE_ALL_LOCAL"}
          >
            Save All
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Loaded {loadedWidgetFiles.length} valid pair(s). Click one row to
            preview.
          </Text>
        </div>
        <Table
          columns={loadedFilesColumns}
          dataSource={loadedWidgetFiles}
          rowKey="key"
          size="small"
          pagination={{ pageSize: 8, showSizeChanger: false }}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedLoadedWidgetKey
              ? [selectedLoadedWidgetKey]
              : [],
            onChange: selectedKeys => {
              setSelectedLoadedWidgetKey(selectedKeys[0] || "");
            },
          }}
          onRow={record => ({
            onClick: () => setSelectedLoadedWidgetKey(record.key),
          })}
        />
        <div style={{ marginTop: 16 }}>
          <Tabs
            activeKey={loadedContentTab}
            onChange={key => setLoadedContentTab(key)}
            items={[
              {
                key: "meta",
                label: "Meta",
                children: (
                  <CodeEditor
                    value={selectedLoadedWidget?.metaContent || ""}
                    editable={false}
                    height="350px"
                  />
                ),
              },
              {
                key: "script",
                label: "Script",
                children: (
                  <CodeEditor
                    value={selectedLoadedWidget?.scriptContent || ""}
                    editable={false}
                    height="350px"
                  />
                ),
              },
            ]}
          />
        </div>
      </Modal>
    </div>
  );
};

export default UiWidgetView;
